import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ProgressCard from '../../../../components/card/ProgressCard'
import PropTypes from 'prop-types'
import ParameterAction from '../actions/ParameterAction'
import ParameterGroupDto from '../dto/ParameterGroupDto'
import ParameterGroupLinkDto from '../dto/ParameterGroupLinkDto'
import { PATH_REFERENCIAL_PARAMETER } from 'home/constants/RouteConstants'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { nbPerPageLabel, REFERENTIAL_JOBSYNC_ID } from 'referencial/constants/ReferencialConstants'
import { getUser } from 'utils/SettingUtils'
import { push } from '@lagunovsky/redux-react-router'
import { PATH_REFERENCIAL_PARAMETERS_GROUP_NEW } from '../../../../home/constants/RouteConstants'
import { CardTable } from 'components/datatable/NewTable'
import { Grid } from '@mui/material'
import JobSyncStatus from '../../JobSyncStatus'
import { reloadJobSync } from '../../../util/ReferencialUtils'
import ReferencialAction from '../../../action/ReferencialAction'
import { Stack } from '@mui/material'
import { flatMap, groupBy, mapValues, uniqBy, partition } from 'lodash'

const ParametersGroupsApp = () => {
    const {
        parameterGroupLinks,
        parameterGroups,
        accountUser,
        contacts,
    } = useSelector(store => ({
        parameterGroupLinks: store.ParameterReducer.parameterGroupLinks,
        parameterGroups: store.ParameterReducer.parameterGroups,
        accountUser: store.AccountReducer.accountUser,
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const [parameterGroupLoaded, setParameterGroupLoaded] = useState(false)
    const [parameterGroupLinksLoaded, setParameterGroupLinksLoaded] = useState(false)
    const [jobSync, setJobSync] = useState(undefined)

    useEffect(() => {
        reloadJobSync(REFERENTIAL_JOBSYNC_ID.parametergroup, setJobSync)
    }, [])

    useEffect(() => {
        if (jobSync && jobSync.executions[0]) {
            if (!jobSync.executions[0].statusCode) {
                setTimeout(() => {
                    reloadJobSync(REFERENTIAL_JOBSYNC_ID.parametergroup, setJobSync)
                }, 5000)
            }
        }
    }, [jobSync])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(ParameterAction.fetchParameterGroups())
        dispatch(ParameterAction.fetchParameterGroupLinks())
        dispatch(ParameterAction.fetchParameters())
    }, [])

    useEffect(() => {
        setParameterGroupLoaded(true)
    }, [parameterGroups])

    useEffect(() => {
        setParameterGroupLinksLoaded(true)
    }, [parameterGroupLinks])

    const isConsultant = useMemo(() => getUser().consultant === '1', [])

    useActions(() => {
        const isEnableEdit = accountUser.metadata === '1' || accountUser.isAdmin === '1'
        const baseActions = {
            referencialActions: {
                actions: {
                    sync: {
                        updateAction: options => dispatch(ReferencialAction.launchSyncJob(REFERENTIAL_JOBSYNC_ID.parametergroup, options, () => reloadJobSync(REFERENTIAL_JOBSYNC_ID.parametergroup, setJobSync))),
                    },
                },
            },
        }
        const newAction = (isEnableEdit && !isConsultant) ? {
            new: () => dispatch(push(PATH_REFERENCIAL_PARAMETERS_GROUP_NEW)),
        } : {}
        return { ...baseActions, ...newAction }
    }, [])

    useTitle(() => [{
        title: i18n.referencials,
        href: 'referencial',
    }, {
        title: i18n.parameterGroup,
        href: PATH_REFERENCIAL_PARAMETER,
    }], [])

    const nbClassParametersByGroup = useMemo(() =>
        mapValues(
            groupBy(parameterGroupLinks, 'classGroupCode'),
            links => uniqBy(links, 'parameterCode').length,
        )
    , [parameterGroupLinks])

    const usageKeys = [
        'usageGroupCode',
        'usageGroupCode2',
        'usageGroupCode3',
        'usageGroupCode4',
        'usageGroupCode5',
    ]

    const nbUsageParametersByGroup = useMemo(() => {
        const allLinks = flatMap(parameterGroupLinks, link =>
            usageKeys.map(k => link[k]).filter(Boolean).map(group => ({ group, link })),
        )
        return mapValues(
            groupBy(allLinks, 'group'),
            arr => uniqBy(arr.map(x => x.link), 'parameterCode').length)
    }, [parameterGroupLinks])

    const [classData, usageData] = useMemo(() => {
        const [classGroups, usageGroups] = partition(
            parameterGroups,
            group => parameterGroupLinks.some(link => link.classGroupCode === group.code),
        )
        return [
            classGroups.map(p => ({
                ...p,
                id: p.code,
                nbClassParameters: nbClassParametersByGroup[p.code] ?? 0,
                nbUsageParameters: nbUsageParametersByGroup[p.code] ?? 0,
            })),
            usageGroups.map(p => ({
                ...p,
                id: p.code,
                nbParameters: nbUsageParametersByGroup[p.code] ?? 0,
            })),
        ]
    }, [parameterGroupLinks, parameterGroups])

    const userEmail = contacts.find(c => c.id === accountUser?.contactCode)?.email
    const displayDev = userEmail?.includes('@aquasys.fr')

    const classHeaders = ['id', 'name', ...(displayDev ? ['nbClassParameters', 'nbUsageParameters'] : [])]
    const usageHeaders = ['id', 'name', ...(displayDev ? ['nbParameters'] : [])]

    if (!parameterGroupLoaded || !parameterGroupLinksLoaded) {
        return <ProgressCard indeterminate />
    }

    return (
        <Stack margin={1} spacing={1}>
            <Grid container justifyContent='flex-end' className='padding-left-1'>
                <JobSyncStatus jobSync={jobSync}/>
            </Grid>
            <CardTable
                color='bannerColor'
                title={i18n.classGroup}
                rows={classData}
                headers={classHeaders}
                rowsPerPageOptions={nbPerPageLabel}
                onClickRow={(element) => {
                    dispatch(push(`/referencial/parameterGroup/${element.id}/dashboard`))
                }}
            />
            <CardTable
                color='bannerColor'
                title={i18n.usageGroup}
                rows={usageData}
                headers={usageHeaders}
                rowsPerPageOptions={nbPerPageLabel}
                onClickRow={(element) => {
                    dispatch(push(`/referencial/parameterGroup/${element.id}/dashboard`))
                }}
            />
        </Stack>
    )
}

ParametersGroupsApp.propTypes = {
    parameterGroupLinks: PropTypes.arrayOf(PropTypes.instanceOf(ParameterGroupLinkDto)),
    parameterGroups: PropTypes.arrayOf(PropTypes.instanceOf(ParameterGroupDto)),
    forceFetch: PropTypes.func,
    fetchParameterGroupLinks: PropTypes.func,
    fetchParameterGroups: PropTypes.func,
}

export default ParametersGroupsApp